import lazyInit from 'util/lazyInit';
import makeDeferred, {Deferred} from 'util/makeDeferred';

export interface ModelFetcher {
  fetchMulti(keys: Array<string>): Promise<void>;
  isKeyLoadedOrLoading(key: string): boolean;
}

/**
 * How the batched fetchMulti call covering a key settled. A rejection counts as failed even without a reason.
 */
export type FetchOutcome = {failed: false} | {failed: true; error: unknown};

interface PendingBatch {
  keys: Array<string>;
  /** Settles with the outcome of the fetchMulti call this batch becomes, and tracks it while in flight. */
  outcome: Deferred<FetchOutcome>;
}

/**
 * Code for dealing with fetching models in an autobatched way.
 *
 * The Autobatcher coalesces multiple requests to load data from the server of the same type into
 * bulk requests.
 *
 * This file exposes a single function, requestFetch(modelFetcher, modelKey), which callers can
 * use to indicate that they want the given resource to be loaded (but they are delegating the exact
 * specifics of that loading to the framework). Autobatcher keeps track of all of these requests in
 * pendingFetchesByClass, then periodically makes one bulk request per type of resource for all
 * collected keys of that resource (excutedBatchedRequestsForPendingFetches).
 *
 * ModelFetchers are classes that contain the data management of a certain type of model. Each
 * ModelFetcher should expose a requestFetch(modelId) and a fetchMulti(modelIds) to be used with
 * Autobatcher.
 *
 * The Autobatcher will not honor a requestFetch if any of the following is true:
 *   - it's already loaded in the store
 *   - there's already plans to fetch it (e.g., it's already inside pendingFetchesByClass)
 *   - it's already being fetch (a request for it is in flight)
 */
class Autobatcher {
  pendingFetchesByClass: Map<ModelFetcher, PendingBatch> = new Map();
  /** In-flight keys, each mapped to the outcome of the fetchMulti call covering it. */
  activeFetchesByClass: Map<ModelFetcher, Map<string, Promise<FetchOutcome>>> = new Map();
  areBatchedRequestsScheduled: boolean = false;
  private scheduledBatchTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.reset();
  }

  /**
   * Clears pending and active fetch bookkeeping. It also cancels any scheduled batch, so pending keys
   * requested before the reset are not fetched. In-flight fetches are not canceled, and their completion does not
   * affect fetches tracked after the reset.
   */
  reset(): void {
    if (this.scheduledBatchTimeout != null) {
      clearTimeout(this.scheduledBatchTimeout);
      this.scheduledBatchTimeout = null;
    }
    // Callers waiting on a canceled batch re-check the store rather than wait for a fetch that won't happen.
    for (const pendingBatch of this.pendingFetchesByClass.values()) {
      pendingBatch.outcome.resolve({failed: false});
    }
    this.pendingFetchesByClass = new Map();
    this.activeFetchesByClass = new Map();
    // Indicates whether we should schedule a new set of batched requests (via
    // performPendingFetches)
    this.areBatchedRequestsScheduled = false;
  }

  /**
   * Returns a promise that settles when the pending or in-flight fetchMulti call covering the given key
   * settles, or null if no fetch for the key is pending or in flight. The promise never rejects; a
   * fetchMulti rejection is reported as a failed FetchOutcome.
   */
  getFetchOutcome(modelFetcher: ModelFetcher, modelKey: string): Promise<FetchOutcome> | null {
    const activeOutcome = this.activeFetchesByClass.get(modelFetcher)?.get(modelKey);
    if (activeOutcome) {
      return activeOutcome;
    }
    const pendingBatch = this.pendingFetchesByClass.get(modelFetcher);
    return pendingBatch?.keys.includes(modelKey) ? pendingBatch.outcome : null;
  }

  isFetchPending(modelFetcher: ModelFetcher, modelKey: string): boolean {
    return Boolean(this.pendingFetchesByClass.get(modelFetcher)?.keys.includes(modelKey));
  }

  isFetchActive(modelFetcher: ModelFetcher, modelKey: string): boolean {
    return Boolean(this.activeFetchesByClass.get(modelFetcher)?.has(modelKey));
  }

  isFetchPendingOrActive(modelFetcher: ModelFetcher, modelKey: string): boolean {
    return this.isFetchPending(modelFetcher, modelKey) || this.isFetchActive(modelFetcher, modelKey);
  }

  addPendingFetch(modelFetcher: ModelFetcher, modelKey: string): void {
    if (!this.isFetchPending(modelFetcher, modelKey) && !this.isFetchActive(modelFetcher, modelKey)) {
      let pendingBatch = this.pendingFetchesByClass.get(modelFetcher);
      if (!pendingBatch) {
        pendingBatch = {keys: [], outcome: makeDeferred<FetchOutcome>()};
        this.pendingFetchesByClass.set(modelFetcher, pendingBatch);
      }
      pendingBatch.keys.push(modelKey);
      if (!this.areBatchedRequestsScheduled) {
        this.scheduledBatchTimeout = setTimeout(this.performPendingFetches);
      }
      this.areBatchedRequestsScheduled = true;
    }
  }

  performPendingFetches = (): void => {
    this.scheduledBatchTimeout = null;
    const pendingFetchesToPerform = this.pendingFetchesByClass;
    this.pendingFetchesByClass = new Map();
    this.areBatchedRequestsScheduled = false;
    for (const [modelFetcher, pendingBatch] of pendingFetchesToPerform.entries()) {
      const {keys: modelKeys, outcome} = pendingBatch;
      // Registered before calling fetchMulti, which may synchronously dispatch actions whose subscribers
      // request the same keys.
      let activeFetches = this.activeFetchesByClass.get(modelFetcher);
      if (!activeFetches) {
        activeFetches = new Map();
        this.activeFetchesByClass.set(modelFetcher, activeFetches);
      }
      for (const key of modelKeys) {
        activeFetches.set(key, outcome);
      }
      const settle = (result: FetchOutcome): void => {
        // Looked up again because reset() may have replaced the map while the fetch was in flight.
        const currentActiveFetches = this.activeFetchesByClass.get(modelFetcher);
        for (const key of modelKeys) {
          if (currentActiveFetches?.get(key) === outcome) {
            currentActiveFetches.delete(key);
          }
        }
        outcome.resolve(result);
      };
      // Calls fetchMulti synchronously and turns a synchronous throw into a rejection.
      const fetchPromise = new Promise<void>((resolve) => {
        resolve(modelFetcher.fetchMulti(modelKeys));
      });
      fetchPromise.then(
        () => {
          settle({failed: false});
        },
        (error: unknown) => {
          settle({failed: true, error});
          // Left unhandled so the global unhandled rejection handler still reports it.
          throw error;
        }
      );
    }
  };
}

export const getAutobatcher = lazyInit<Autobatcher>(
  () => new Autobatcher(),
  (autobatcher) => {
    autobatcher.reset();
  }
);

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface requestFetchOptions {
  forceFetch: boolean;
}

/**
 * Add this model to the list of models to fetch asynchronously if they're not already in the store
 * or in the process of being fetched.
 */
export function requestFetch(
  modelFetcher: ModelFetcher,
  modelKey: string,
  {forceFetch}: requestFetchOptions = {forceFetch: false}
): void {
  // Ideally we would avoid fetching if the model is currently being fetched ("loading"), but currently
  // `isKeyLoadedOrLoading` combines the loaded and loading states
  if (!forceFetch && modelFetcher.isKeyLoadedOrLoading(modelKey)) {
    // Don't add a pending fetch if the model manager reports that the key's already loaded or
    // already loading (request in flight)
    return;
  }
  getAutobatcher().addPendingFetch(modelFetcher, modelKey);
}
